import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, FileText } from "lucide-react";
import { useState } from "react";

interface PromptDialogProps {
  open: boolean;
  onClose: () => void;
  prompt: string;
  rawOutput?: string;
}

export const PromptDialog = ({ open, onClose, prompt, rawOutput }: PromptDialogProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (prompt) {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent
        className="max-w-[90vw] w-full max-h-[80vh] overflow-y-auto"
        style={{ width: "90vw", maxWidth: "90vw", maxHeight: "80vh" }}
      >
        <DialogHeader>
          <DialogTitle>Prompt & Raw Output</DialogTitle>
        </DialogHeader>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-6 h-[60vh]">
          {/* Prompt column */}
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-2">
              <div className="font-semibold mb-2">Prompt</div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 ml-1"
                onClick={handleCopy}
                disabled={copied}
                aria-label={copied ? "Copied" : "Copy prompt to clipboard"}
                title={copied ? "Copied" : "Copy prompt to clipboard"}
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <FileText className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {prompt ? (
                <pre className="bg-muted p-3 rounded text-sm whitespace-pre-wrap overflow-x-auto h-full">{prompt}</pre>
              ) : (
                <div className="text-muted-foreground">No prompt available</div>
              )}
            </div>
          </div>
          {/* Raw Output column */}
          <div className="flex flex-col h-full">
            <div className="font-semibold mb-2">Raw Output</div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {rawOutput ? (
                <pre className="bg-muted p-3 rounded text-sm whitespace-pre-wrap overflow-x-auto h-full">
                  {rawOutput}
                </pre>
              ) : (
                <div className="text-muted-foreground">No raw output available</div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
